import { UAEntityTypes } from '@unifyapps/defs/types/entities';
import type {
  WalkthroughSession,
  WalkthroughSessionEntity,
} from '@unifyapps/defs/types/walkthroughSession';
import useEventCallback from '@unifyapps/hooks/useEventCallback';
import { useCreateEntity } from '@unifyapps/network/generated/entity-rest-api/entity-rest-api';
import type { EntityProperties } from '@unifyapps/network/generated/models/entityProperties';
import useMutationHelpers from '../../../hooks/useMutationHelpers';
import useUpdateEntityWithVersionError from '../../../hooks/useUpdateEntityWithVersionError';

type CreateWalkthroughSessionParams = {
  walkthroughId: string;
  interfaceId: string;
  sessionId?: string;
};

function useCreateWalkthroughSession() {
  const { mutateAsync: createEntity, isSuccess, isPending } = useCreateEntity();
  const { onMutationError } = useMutationHelpers();

  const createWalkthroughSession = useEventCallback(
    async ({ walkthroughId, interfaceId, sessionId }: CreateWalkthroughSessionParams) => {
      const newWalkthroughSession: WalkthroughSession = {
        interfaceId,
        walkthroughId,
        status: 'in_progress',
        ...(sessionId && { sessionId }),
      };

      const walkthroughSessionEntity = await createEntity(
        {
          data: {
            entityType: UAEntityTypes.WalkthroughSession,
            properties: newWalkthroughSession as unknown as EntityProperties,
          },
        },
        {
          onError: onMutationError,
        },
      );

      return walkthroughSessionEntity as WalkthroughSessionEntity | undefined;
    },
  );

  return {
    isCreatePending: isPending,
    isCreateSuccess: isSuccess,
    createWalkthroughSession,
  };
}

function useUpdateWalkthroughSession() {
  const {
    isPending: isUpdatePending,
    isSuccess: isUpdateSuccess,
    mutateAsync: updateMutateAsync,
  } = useUpdateEntityWithVersionError();

  const { onMutationError } = useMutationHelpers();

  const updateWalkthroughSession = useEventCallback(
    async (walkthroughSession: WalkthroughSessionEntity) => {
      const updatedWalkthroughSession = await updateMutateAsync(
        {
          data: walkthroughSession,
        },
        {
          onError: onMutationError,
        },
      );

      return updatedWalkthroughSession as WalkthroughSessionEntity | undefined;
    },
  );

  return {
    isUpdatePending,
    isUpdateSuccess,
    updateWalkthroughSession,
  };
}

export { useCreateWalkthroughSession, useUpdateWalkthroughSession };
